<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('stockIn.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('stockIn.pageTitle'),
            href: '/admin/stock-in'
          },
          {
            text: $t('create'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <div class="ts-border ts-border-gray-300 ts-rounded-lg ts-p-3">
            <form class="form-horizontal">
              <div class="row">
                <div class="col-md-6 ts-space-y-3">
                  <div class="row">
                    <label class="col-md-3 form-col-label control-label">{{
                      $t('stockIn.purchaseNumber')
                    }}</label>
                    <div class="col-md-7">
                      <Ponumber
                        v-model="model"
                        :validate="errors"
                        @loading="value => (loading = value)"
                      />
                    </div>
                  </div>
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label required"
                      >{{ $t('stockIn.invoiceNumber') }}</label
                    >
                    <div class="col-md-7">
                      <input
                        v-model="model.invoice_no"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors.has('invoice_no') }"
                      />
                      <div
                        class="invalid-feedback"
                        v-if="errors.has('invoice_no')"
                      >
                        {{ errors.first('invoice_no') }}
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label required"
                      >{{ $t('stockIn.location') }}</label
                    >
                    <div class="col-md-7">
                      <a-select
                        class="ts-w-full"
                        v-model="model.location_id"
                        :options="locationList"
                        showSearch
                        :disabled="true"
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        :class="{ 'is-invalid': errors.has('location_id') }"
                      ></a-select>
                      <div
                        class="invalid-feedback"
                        v-if="errors.has('location_id')"
                      >
                        {{ errors.first('location_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label required"
                      >{{ $t('stockIn.vendor') }}</label
                    >
                    <div class="col-md-7">
                      <a-select
                        v-model="model.vendor_id"
                        :options="vendorList"
                        showSearch
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        @change="onVendorChange"
                        class="ts-w-full"
                        :class="{ 'is-invalid': errors.has('vendor_id') }"
                      ></a-select>
                      <div
                        class="invalid-feedback"
                        v-if="errors.has('vendor_id')"
                      >
                        {{ errors.first('vendor_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <label
                      class="form-col-label control-label col-md-3"
                    ></label>
                    <div class="col-md-7">
                      <div>
                        {{ $t('wholesale.creditAmount') }} :
                        <span class="ts-ml-3 ts-text-red-600">{{
                          creditAmount
                            ? $defaultCurrencyWithCode(
                                creditAmount,
                                default_precision
                              )
                            : 0
                        }}</span>
                      </div>
                      <div class="ts-mt-3 ts-mb-0">
                        {{ $t('wholesale.creditDay') }} :
                        <input
                          v-model.number="model.credit_day"
                          type="number"
                          class="ts-ml-3 ts-p-1 ts-border ts-outline-none"
                          style="border-radius: 4px; width: 150px"
                        />
                        <div class="validate" v-if="errors.has('credit_day')">
                          {{ errors.first('credit_day') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 ts-space-y-3">
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label required"
                      >{{ $t('stockIn.stockInDate') }}</label
                    >
                    <div class="col-md-7">
                      <a-date-picker
                        class="ts-w-full"
                        format="DD-MM-YYYY"
                        valueFormat="DD-MM-YYYY"
                        v-model="model.stock_in_date"
                        :class="{ 'is-invalid': errors.has('stock_in_date') }"
                      ></a-date-picker>
                      <div
                        class="invalid-feedback"
                        v-if="errors.has('stock_in_date')"
                      >
                        {{ errors.first('stock_in_date') }}
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <label class="col-md-3 form-col-label control-label">{{
                      $t('stockIn.nextPaymentDate')
                    }}</label>
                    <div class="col-md-7">
                      <a-date-picker
                        class="ts-w-full"
                        format="DD-MM-YYYY"
                        valueFormat="DD-MM-YYYY"
                        v-model="model.next_payment_date"
                        :class="{
                          'is-invalid': errors.has('next_payment_date')
                        }"
                      ></a-date-picker>
                      <div
                        class="invalid-feedback"
                        v-if="errors.has('next_payment_date')"
                      >
                        {{ errors.first('next_payment_date') }}
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <label class="form-col-label control-label col-md-3">{{
                      $t('stockIn.purchaseType')
                    }}</label>
                    <div class="col-md-7">
                      <a-select
                        v-model="model.purchase_type_id"
                        :options="purchaseTypeList"
                        showSearch
                        :filterOption="
                          (input, option) =>
                            option.componentOptions.children[0].text
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                        "
                        class="ts-w-full"
                      ></a-select>
                      <div
                        class="validate"
                        v-if="errors.has('purchase_type_id')"
                      >
                        {{ errors.first('purchase_type_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <label
                      class="col-md-3 form-col-label control-label required"
                      >{{ $t('stockIn.fixRate') }}</label
                    >
                    <div class="col-md-7">
                      <input
                        v-model="model.fix_rate"
                        type="text"
                        readonly
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="row">
                    <label class="col-md-3 form-col-label control-label">{{
                      $t('stockIn.remark')
                    }}</label>
                    <div class="col-md-7">
                      <textarea
                        v-model="model.remarks"
                        rows="3"
                        class="form-control ts-w-full"
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div
            class="ts-border ts-border-gray-300 ts-rounded-lg ts-p-3 ts-mt-4"
          >
            <ProductDetail v-model="model" :validate="errors" />
          </div>
          <div class="row ts-mt-4">
            <div class="col-md-12 ts-space-x-4 ts-flex ts-justify-end">
              <ts-button @click.prevent="$router.push({ name: 'stock-in' })">{{
                $t('cancel')
              }}</ts-button>
              <ts-button
                color="primary"
                outline
                :waiting="waiting_new"
                @click.prevent="saveAddNew"
                >{{ $t('savePrint') }}</ts-button
              >
              <ts-button
                color="primary"
                :waiting="waiting"
                @click.prevent="save"
                >{{ $t('save') }}</ts-button
              >
            </div>
          </div>
        </ts-loading-banner>
        <a-modal
          :title="$t('pdf')"
          width="50%"
          v-model="show_pdf"
          :footer="null"
        >
          <ts-preview-pdf v-model="waiting_new" :src="src" />
        </a-modal>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import Ponumber from './components/purchase-order-number'
import ProductDetail from './components/product-detail'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'stockInCreate',
  components: {
    Ponumber,
    ProductDetail
  },
  data () {
    return {
      errors: new Errors(),
      loading: false,
      waiting_new: false,
      waiting: false,
      show_pdf: false,
      src: null,
      model: {
        stock_in_id: null,
        location_id: null,
        invoice_no: null,
        stock_in_date: moment().format('DD-MM-YYYY'),
        po_id: null,
        vendor_id: null,
        fix_rate: 4000,
        product_detail: [],
        remarks: null,
        purchase_type_id: null,
        credit_day: 0,
        next_payment_date: null
      }
    }
  },
  async created () {
    this.loading = true
    await this.fetchResource().finally(() => (this.loading = false))
    this.autoSelectMainWarehouse()
  },
  computed: {
    ...mapState('stock/stockIn', ['locations']),
    vendorList () {
      return this.$store.state.stock.stockIn.suppliers.map(el => ({
        value: el.vendor_id,
        label: el.vendor_name,
        credit_day: el.credit_day,
        credit_amount: el.credit_amount
      }))
    },
    locationList () {
      return this.$store.state.stock.stockIn.locations.map(el => ({
        value: el.location_id,
        label: el.location_name_en
      }))
    },
    purchaseTypeList () {
      return this.$store.state.stock.stockIn.purchaseTypes.map(el => ({
        value: el.purchase_type_id,
        label: el.purchase_type
      }))
    },
    default_precision () {
      return this.$store.state.authUser.sys_setting.decimal_digit
    },
    creditAmount () {
      let vendor = this.vendorList.find(v => v.value == this.model.vendor_id)
      if (vendor) {
        return vendor.credit_amount
      }
      return 0
    }
  },
  methods: {
    ...mapActions('stock/stockIn', ['fetchResource', 'store']),
    save () {
      this.waiting = true
      this.errors = new Errors()
      this.store(this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'stock-in' })
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    saveAddNew () {
      this.waiting_new = true
      this.show_pdf = true
      this.errors = new Errors()
      this.store(this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.src = response.url
          this.clearInput()
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
          this.show_pdf = false
        })
        .finally(() => {
          this.waiting_new = false
        })
    },
    autoSelectMainWarehouse () {
      this.model.location_id = this.$store.state.authUser.user.warehouses
        .filter(w => w.pivot.is_default == true)
        .reduce((i, v) => (i = v.location_id), null)
    },
    onVendorChange (value, opt) {
      this.model.credit_day = opt.data.props.credit_day
    },
    updateNextPaymentDate () {
      if (this.model.stock_in_date && this.model.credit_day > 0) {
        this.model.next_payment_date = moment(
          this.model.stock_in_date,
          'DD-MM-YYYY'
        )
          .add(this.model.credit_day, 'days')
          .format('DD-MM-YYYY')
      } else {
        this.model.next_payment_date = null
      }
    },
    clearInput () {
      this.model.stock_in_id = null
      this.model.location_id = null
      this.model.invoice_no = null
      ;(this.model.stock_in_date = moment().format('DD-MM-YYYY')),
        (this.model.po_id = null)
      this.model.vendor_id = null
      this.model.fix_rate = 4000
      this.model.remarks = null
      this.model.purchase_type_id = null
      this.model.credit_day = 0
      this.model.next_payment_date = null
      this.model.product_detail = [
        {
          product_id: null,
          uom_id: null,
          qty: 1,
          cost: 0,
          discount_rate: 0,
          discount_type: '%',
          total_after_discount: null,
          vat_rate: 0,
          vat_amount: 0,
          grand_total: 0,
          expiry_date: null,
          is_expire: false,
          product_grade: ''
        }
      ]
      this.errors = new Errors()
    }
  },
  watch: {
    'model.stock_in_date': function () {
      this.updateNextPaymentDate()
    },
    'model.credit_day': function () {
      this.updateNextPaymentDate()
    }
  },
  beforeRouteLeave (to, from, next) {
    this.$store.commit('stock/stockIn/CLEAR_SETUP')
    next()
  }
}
</script>
